import React, { useState, useEffect } from 'react';
import { Modal, Slider, message } from 'antd';
import { theme } from 'utils/style/theme';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import iconClose from 'utils/icon/icon_close_circle.svg';
import { handleMessage } from 'utils/helper/message';
import PrimaryButton from 'components/button/PrimaryButton';

import SelectBox from 'components/SelectBox';
import { soundApis } from 'apis/resource/soundApis';
import InputStyled from 'components/InputStyled';
import ReplaceImage from './ReplaceSound';
import { ICommonCode } from 'utils/types/storyTypes';

/**
 * * 리소스 - 미니컷 등록 모달
 */

interface IProps {
  visible: {
    create: boolean;
    edit: boolean;
    delete: boolean;
    name: string;
  };
  setVisible: (e: any) => void;
  setRefresh: React.Dispatch<React.SetStateAction<boolean>>;
  refresh: boolean;
  projectId: string | undefined;
  charactersData: ICommonCode[] | null;
  soundTypeData: ICommonCode[] | null;
  data: any;
  setData: React.Dispatch<any>;
}

const SoundEditModal = ({
  visible,
  setVisible,
  setRefresh,
  refresh,
  projectId,
  charactersData,
  soundTypeData,
  data,
  setData,
}: IProps) => {
  const { t } = useTranslation();

  const [soundFile, setSoundFile] = useState<any>(null);

  const [loading, setLoading] = useState(false);

  const [volumeValue, setVolumeValue] = useState<number>(0);
  useEffect(() => {
    if (data) {
      setVolumeValue(data.game_volume * 100);
    }
  }, [data]);

  /// 서버 전송
  const handleSubmit = async () => {
    if (projectId) {
      try {
        const formData = new FormData();
        if (soundFile) {
          formData.append(`file`, soundFile);
          formData.append(`sound_type`, data.sound_type);
          formData.append(`sound_name`, data.sound_name);
          formData.append(`speaker`, data.speaker);
        } else {
          formData.append(`sound_type`, data.sound_type);
          formData.append(`sound_name`, data.sound_name);
          formData.append(`speaker`, data.speaker);
        }

        setLoading(true);
        await soundApis.soundUpdate(data.project_id, data.id, formData);
        setRefresh(!refresh);
        setLoading(false);
        handleClose();
      } catch (err) {
        setLoading(false);
        handleMessage(message, 'error', 'Upload failed');
      }
    }
  };

  const handleOnChange = (value, type: string) => {
    const cloneData = JSON.parse(JSON.stringify(data));
    cloneData[type] = value;
    setData(cloneData);
  };

  /// 모달 닫기
  const handleClose = () => {
    setVisible({ ...visible, edit: false });
  };
  return (
    <CustomModal
      title={t(
        'storyEpisodeID.storyTabMenuList.resourceContent.resourceTabMenuList.soundContent.editModal.title',
      )}
      visible={visible.edit}
      onCancel={handleClose}
      footer={
        <ButtonWrapper>
          <PrimaryButton
            text={t('buttons.save')}
            height='40px'
            width='fit-content'
            bgColor={theme.colors.blue_500}
            textColor='#ffffff'
            fontWeight={theme.fontWeights.medium}
            fontSize={theme.fontSizes.normal}
            border={`1px solid ${theme.colors.blue_500}`}
            rounded='6px'
            hoverBgColor={theme.colors.blue_600}
            activeBgColor={theme.colors.blue_800}
            selectedBgColor={theme.colors.blue_700}
            onClick={handleSubmit}
            loading={loading}
            disabled={loading}
          />
        </ButtonWrapper>
      }
      closeIcon={
        <img src={iconClose} width={20} height={20} alt='icon-close' />
      }
    >
      <ModalWrapper>
        <HeaderWrapper>
          <p>ID: {data?.id}</p>
        </HeaderWrapper>
        <div>
          <p className='title'>
            {t(
              'storyEpisodeID.storyTabMenuList.resourceContent.resourceTabMenuList.soundContent.editModal.soundName',
            )}
          </p>
          <InputStyled
            value={data?.sound_name}
            onChange={(e) => {
              handleOnChange(e.target.value, 'sound_name');
            }}
            maxLength={30}
          />
          <p className='length-subtext'>
            {typeof data?.image_name?.length === 'number'
              ? `${data?.image_name?.length}/30 characters`
              : '0/30 characters'}
          </p>
        </div>
        <div>
          <p className='title'>
            {t(
              'storyEpisodeID.storyTabMenuList.resourceContent.resourceTabMenuList.soundContent.editModal.soundType',
            )}
          </p>
          <SelectBox
            value={data?.sound_type}
            onChange={(e) => {
              handleOnChange(e, 'sound_type');
            }}
            options={soundTypeData?.map((v, i) => {
              return {
                value: v.code,
                label: v.code_name,
              };
            })}
          />
        </div>
        <div>
          <p className='title'>
            {t(
              'storyEpisodeID.storyTabMenuList.resourceContent.resourceTabMenuList.soundContent.editModal.character',
            )}
          </p>
          <SelectBox
            value={data?.speaker}
            onChange={(e) => {
              handleOnChange(e, 'speaker');
            }}
            options={charactersData?.map((v, i) => {
              return {
                value: v.code,
                label: v.code_name,
              };
            })}
          />
        </div>
        <div>
          <p className='title'>
            {t(
              'storyEpisodeID.storyTabMenuList.resourceContent.resourceTabMenuList.soundContent.editModal.volume',
            )}
          </p>
          <StyledSlider
            defaultValue={volumeValue}
            min={0}
            max={100}
            onChange={(value) => {
              handleOnChange(value / 100, 'game_volume');
            }}
            trackStyle={{ backgroundColor: theme.colors.blue_500 }}
            handleStyle={{
              borderColor: theme.colors.blue_500,
              backgroundColor: theme.colors.blue_500,
              color: theme.colors.blue_500,
            }}
          />
        </div>
        <ReplaceImage
          setSoundFile={setSoundFile}
          requiredSubtitle={
            <p className='turText'>
              {t(
                'storyEpisodeID.storyTabMenuList.resourceContent.resourceTabMenuList.soundContent.requiredSubtitle',
              )}
            </p>
          }
        />
      </ModalWrapper>
    </CustomModal>
  );
};

export default SoundEditModal;

const CustomModal = styled(Modal)`
  .ant-modal-body {
    padding: 0;
  }
  .ant-modal-content {
    box-shadow: none;
    width: 566px;
  }
  .ant-radio-wrapper {
    span {
      font-size: ${theme.fontSizes.normal};
      font-weight: 400;
      line-height: 24px;
      letter-spacing: 0.2px;
      color: ${theme.colors.dark_500};
    }
  }
  .ant-modal-footer {
    margin-top: 10px !important;
  }
  ul {
    margin-left: 25px;
  }
  .ant-modal-close:hover {
    background-color: transparent;
  }
`;

const ModalWrapper = styled.div`
  & > * + * {
    margin-top: 8px;
  }
`;

const ButtonWrapper = styled.div`
  display: flex;
  justify-content: end;
`;
const HeaderWrapper = styled.div`
  display: flex;
  justify-content: center;
  p {
    color: ${theme.colors.dark_500};
    font-weight: ${theme.fontWeights.bold};
    font-size: ${theme.fontSizes.md};
  }
`;

const StyledSlider = styled(Slider)`
  .ant-slider-handle::after {
    background-color: ${(props) => props.theme.colors.blue_500};
    border-color: ${(props) => props.theme.colors.blue_500};
    box-shadow: ${(props) => props.theme.colors.blue_500};
  }
`;
